import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { adminApi, useAdminAuth } from './adminAuth';
import { useToast } from '../hooks/use-toast';

const AdminNotificationsContext = createContext(null);

const TYPE_LABEL = {
  service_request: 'Service Request',
  property_submission: 'Property Submission',
  property_interest: 'Property Interest',
};

export const AdminNotificationsProvider = ({ children }) => {
  const { token } = useAdminAuth();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [byType, setByType] = useState({});
  const [loading, setLoading] = useState(false);
  const seenIds = useRef(new Set());
  const firstLoad = useRef(true);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await adminApi.get('/notifications', { params: { limit: 30 } });
      const newItems = data.items || [];
      // Detect new arrivals since last poll (skip first fetch)
      if (!firstLoad.current) {
        const brandNew = newItems.filter((n) => !seenIds.current.has(n.id));
        brandNew.slice(0, 3).forEach((n) => {
          toast({
            title: n.title || 'New notification',
            description: n.body || TYPE_LABEL[n.type] || '',
          });
        });
      } else {
        firstLoad.current = false;
      }
      newItems.forEach((n) => seenIds.current.add(n.id));
      setItems(newItems);
      setUnread(data.unread || 0);
      setByType(data.unread_by_type || {});
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  }, [token, toast]);

  // Initial + polling every 20s
  useEffect(() => {
    if (!token) { setItems([]); setUnread(0); setByType({}); firstLoad.current = true; seenIds.current.clear(); return; }
    fetchList();
    const iv = setInterval(fetchList, 20000);
    return () => clearInterval(iv);
  }, [token, fetchList]);

  const markAllRead = useCallback(async () => {
    try {
      await adminApi.post('/notifications/mark-read', { all: true });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
      setByType({});
    } catch {}
  }, []);

  const markRead = useCallback(async (ids) => {
    try {
      await adminApi.post('/notifications/mark-read', { ids });
      setItems((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n)));
      fetchList();
    } catch {}
  }, [fetchList]);

  const removeNotification = useCallback(async (id) => {
    try {
      await adminApi.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
      fetchList();
    } catch {}
  }, [fetchList]);

  const value = useMemo(() => ({ items, unread, byType, loading, refresh: fetchList, markAllRead, markRead, removeNotification }), [items, unread, byType, loading, fetchList, markAllRead, markRead, removeNotification]);

  return <AdminNotificationsContext.Provider value={value}>{children}</AdminNotificationsContext.Provider>;
};

export const useAdminNotifications = () => {
  const ctx = useContext(AdminNotificationsContext);
  if (!ctx) return { items: [], unread: 0, byType: {}, loading: false, refresh: () => {}, markAllRead: () => {}, markRead: () => {}, removeNotification: () => {} };
  return ctx;
};
